/**
 * Sponsor placements (§7): always labelled "Sponsored" / "Presented by", never covering controls or the CTA.
 * `useViewability` fires the impression callback once, when ≥50% of the placement is visible for ≥1s (§7.1
 * viewability rule). Clicks carry placement + campaign identifiers.
 */
import { useEffect, useRef, type ReactNode, type RefObject } from 'react';
import { cn } from '../cn';

export interface SponsorRef {
  id: string;
  name: string;
  logoUrl: string;
  /** Dark-mode logo variant (§7 "logo (light and dark variants)"). */
  logoUrlDark?: string;
  tier: 'TITLE' | 'GOLD' | 'SUPPORTING';
  href?: string;
  tagline?: string;
}

export interface Placement {
  /** e.g. 'landing.strip', 'wheel.hub', 'win.credit' */
  placementId: string;
  campaignId?: string;
}

export interface ViewabilityOptions {
  threshold?: number; // default 0.5
  dwellMs?: number; // default 1000
  enabled?: boolean;
}

/** Fires `onImpression` once after the element is ≥threshold visible for ≥dwellMs. */
export function useViewability<T extends HTMLElement>(
  onImpression: () => void,
  { threshold = 0.5, dwellMs = 1000, enabled = true }: ViewabilityOptions = {},
): RefObject<T | null> {
  const ref = useRef<T>(null);
  const fired = useRef(false);
  const cb = useRef(onImpression);
  cb.current = onImpression;
  useEffect(() => {
    const el = ref.current;
    if (!el || !enabled || fired.current || typeof IntersectionObserver === 'undefined') return;
    let timer: number | null = null;
    const io = new IntersectionObserver(
      (entries) => {
        const e = entries[0];
        if (!e) return;
        if (e.isIntersecting && e.intersectionRatio >= threshold && !document.hidden) {
          timer ??= window.setTimeout(() => {
            if (fired.current) return;
            fired.current = true;
            cb.current();
            io.disconnect();
          }, dwellMs);
        } else if (timer != null) {
          window.clearTimeout(timer);
          timer = null;
        }
      },
      { threshold: [0, threshold, 1] },
    );
    io.observe(el);
    return () => {
      if (timer != null) window.clearTimeout(timer);
      io.disconnect();
    };
  }, [threshold, dwellMs, enabled]);
  return ref;
}

export interface SponsorLogoProps {
  sponsor: SponsorRef;
  className?: string;
}

export function SponsorLogo({ sponsor, className }: SponsorLogoProps) {
  return (
    <picture>
      {sponsor.logoUrlDark ? (
        <source srcSet={sponsor.logoUrlDark} media="(prefers-color-scheme: dark)" />
      ) : null}
      <img
        className={cn('wd-sponsor-logo', className)}
        src={sponsor.logoUrl}
        alt={sponsor.name}
        loading="lazy"
        decoding="async"
      />
    </picture>
  );
}

export interface SponsorLockupProps extends Placement {
  sponsor: SponsorRef;
  label?: string;
  onImpression?: (p: Placement & { sponsorId: string }) => void;
  onClick?: (p: Placement & { sponsorId: string }) => void;
  className?: string;
}

/** "Presented by" lockup for the title sponsor (§7 Title tier). */
export function SponsorLockup({
  sponsor,
  label = 'Presented by',
  placementId,
  campaignId,
  onImpression,
  onClick,
  className,
}: SponsorLockupProps) {
  const ref = useViewability<HTMLDivElement>(
    () => onImpression?.({ placementId, campaignId, sponsorId: sponsor.id }),
    {
      enabled: Boolean(onImpression),
    },
  );
  const inner = (
    <>
      <span className="wd-sponsor-lockup__label">{label}</span>
      <picture>
        {sponsor.logoUrlDark ? (
          <source srcSet={sponsor.logoUrlDark} media="(prefers-color-scheme: dark)" />
        ) : null}
        <img className="wd-sponsor-lockup__logo" src={sponsor.logoUrl} alt={sponsor.name} />
      </picture>
    </>
  );
  return (
    <div ref={ref} className={cn('wd-sponsor-lockup', className)} data-placement={placementId}>
      {sponsor.href ? (
        <a
          href={sponsor.href}
          className="wd-sponsor-lockup"
          rel="sponsored noopener"
          target="_blank"
          onClick={() => onClick?.({ placementId, campaignId, sponsorId: sponsor.id })}
        >
          {inner}
        </a>
      ) : (
        inner
      )}
    </div>
  );
}

export interface SponsorStripProps extends Placement {
  sponsors: SponsorRef[];
  label?: string;
  onImpression?: (p: Placement & { sponsorId: string }) => void;
  onClick?: (p: Placement & { sponsorId: string }) => void;
  className?: string;
}

function StripItem({
  sponsor,
  placementId,
  campaignId,
  onImpression,
  onClick,
}: { sponsor: SponsorRef } & Placement & Pick<SponsorStripProps, 'onImpression' | 'onClick'>) {
  const ref = useViewability<HTMLLIElement>(
    () => onImpression?.({ placementId, campaignId, sponsorId: sponsor.id }),
    {
      enabled: Boolean(onImpression),
    },
  );
  const content = <SponsorLogo sponsor={sponsor} />;
  return (
    <li ref={ref} className="wd-sponsor-strip__item" data-tier={sponsor.tier}>
      {sponsor.href ? (
        <a
          href={sponsor.href}
          rel="sponsored noopener"
          target="_blank"
          aria-label={`${sponsor.name} (sponsor)`}
          onClick={() => onClick?.({ placementId, campaignId, sponsorId: sponsor.id })}
        >
          {content}
        </a>
      ) : (
        content
      )}
    </li>
  );
}

/** Sponsor strip (§7.1): weighted order is decided by the API; the kit renders what it is given. */
export function SponsorStrip({
  sponsors,
  label = 'Sponsored',
  placementId,
  campaignId,
  onImpression,
  onClick,
  className,
}: SponsorStripProps) {
  if (sponsors.length === 0) return null;
  return (
    <aside className={cn('wd-sponsor-strip', className)} aria-label="Sponsors" data-placement={placementId}>
      <span className="wd-sponsor-strip__label">{label}</span>
      <ul className="wd-sponsor-strip__list">
        {sponsors.map((s) => (
          <StripItem
            key={s.id}
            sponsor={s}
            placementId={placementId}
            campaignId={campaignId}
            onImpression={onImpression}
            onClick={onClick}
          />
        ))}
      </ul>
    </aside>
  );
}

export interface SponsoredBadgeProps {
  sponsorName: string;
  label?: string;
  children?: ReactNode;
  className?: string;
}

/** "Sponsored by X" badge on a prize (§7 Gold tier). */
export function SponsoredBadge({ sponsorName, label = 'Sponsored by', className }: SponsoredBadgeProps) {
  return (
    <span className={cn('wd-sponsored-badge', className)}>
      {label} {sponsorName}
    </span>
  );
}
